import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Button } from "components/button";
import { Avatar } from "components/avatar";
import { useModalContext } from "components/modal";
import { useHistory } from "react-router-dom";
import Table from "components/table";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Input } from "components/input";
import { useSnackbarContext } from "components/snackbar";
import { useTranslation } from 'react-i18next';
import * as Yup from "yup";
import { Formik, Form } from "formik";
import Select from "components/react-select";

const Con = styled.div`
  padding: 1rem;
`;

const Title = styled.div`
  text-align: left;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`;

const ModalContent = styled.div`
  padding: 1rem;
  min-width: 400px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  > div {
    flex: 1;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

type User = {
  user_id: string;
  full_name: string;
  username: string;
  email: string;
  job_title_label: string;
  division: string;
  status_inactive: boolean;
  owner: boolean;
};

type Business = {
  id: string;
  name: string;
  user_count: number;
  // Add other business fields as needed
};

type UpdateUserData = {
  username?: string;
  newPassword?: string;
  confirmPassword?: string;
};

export default function UserManagement() {
  const { t } = useTranslation();
  const tBase = "views.admin.userManagement";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);

  const fetchUsers = useQuery(
    ["superAdminUsers", selectedBusiness],
    async () => {
      if (!selectedBusiness) {
        return [];
      }
      return await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/users`,
          {
            params: {
              businessId: selectedBusiness
            }
          }
        )
        .then((res) => res.data);
    },
    {
      enabled: !!selectedBusiness
    }
  );

  const fetchBusinesses = useQuery(
    "superAdminBusinesses",
    async () => {
      const businesses = await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/businesses`
        )
        .then((res) => res.data);
      
      return businesses;
    }
  );

  const updateUserMutation = useMutation(
    async ({ userId, data }: { userId: string; data: UpdateUserData }) => {
      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/user/${userId}`,
        { username: data.username }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["superAdminUsers", selectedBusiness]);
        setModal();
        showSnackbar(tr("User updated successfully!"));
        setSelectedUser(null);
      },
      onError: (error: any) => {
        if (error.response?.data === "username") {
          showSnackbar(tr("Username already exists"), "error");
        } else if (error.response?.status === 404) {
          showSnackbar(tr("User not found"), "error");
        } else {
          showSnackbar(tr("Failed to update user"), "error");
        }
      },
    }
  );

  const updatePasswordMutation = useMutation(
    async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/password`,
        {
          userId,
          newPassword,
        }
      );
    },
    {
      onSuccess: () => {
        setModal();
        showSnackbar(tr("Password updated successfully!"));
        setSelectedUser(null);
      },
      onError: (error: any) => {
        if (error.response?.status === 404) {
          showSnackbar(tr("User not found"), "error");
        } else if (error.response?.status === 400) {
          showSnackbar(tr("Password must be at least 4 characters long"), "error");
        } else {
          showSnackbar(tr("Failed to update password"), "error");
        }
      },
    }
  );

  const changeCompanyMutation = useMutation(
    async ({ userId, newCompanyId }: { userId: string; newCompanyId: string }) => {
      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/changeCompany`,
        {
          userId,
          newCompanyId,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["superAdminUsers", selectedBusiness]);
        setModal();
        showSnackbar(tr("User moved to new company successfully!"));
        setSelectedUser(null);
      },
      onError: (error: any) => {
        if (error.response?.status === 404) {
          showSnackbar(tr("User not found"), "error");
        } else {
          showSnackbar(tr("Failed to move user to new company"), "error");
        }
      },
    }
  );

  const handleUpdateUser = (user: User) => {
    setSelectedUser(user);
    setModal({
      component: <UpdateUserModal user={user} onUpdate={updateUserMutation} />,
      label: tr("Update User"),
      props: { shouldCloseOnOverlayClick: false },
    });
  };

  const handleUpdatePassword = (user: User) => {
    setSelectedUser(user);
    setModal({
      component: <UpdatePasswordModal user={user} onUpdate={updatePasswordMutation} />,
      label: tr("Update Password"),
      props: { shouldCloseOnOverlayClick: false },
    });
  };

  const handleChangeCompany = (user: User) => {
    setSelectedUser(user);
    setModal({
      component: <ChangeCompanyModal user={user} onUpdate={changeCompanyMutation} businesses={fetchBusinesses?.data || []} />,
      label: tr("Change Company"),
      props: { shouldCloseOnOverlayClick: false },
    });
  };

  const columns = {
    name: {
      label: tr("Name"),
      cell: (row: User) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar showLabel user_id={row?.user_id}>
            {row?.full_name}
          </Avatar>
          {row.owner && <span className="text-blue-600 bg-blue-100 px-2 py-1 text-xs rounded ml-2">{tr("Owner")}</span>}
          {row.status_inactive && (
            <span className="text-red-600 bg-red-100 px-2 py-1 text-xs rounded ml-2">
              {tr("Inactive")}
            </span>
          )}
        </div>
      ),
    },
    username: {
      label: tr("Username"),
      cell: (row: User) => (
        <div style={{ textTransform: "none" }}>
          {row.username}
        </div>
      ),
    },
    email: {
      label: tr("Email"),
      cell: (row: User) => (
        <div style={{ textTransform: "none" }}>
          {row.email}
        </div>
      ),
    },
    job_title_label: {
      label: tr("Job Title"),
    },
    division: {
      label: tr("Division"),
    },
    actions: {
      label: tr("Actions"),
      cell: (row: User) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleUpdateUser(row);
            }}
          >
            {tr("Edit User")}
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleUpdatePassword(row);
            }}
          >
            {tr("Change Password")}
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleChangeCompany(row);
            }}
          >
            {tr("Change Company")}
          </Button>
        </div>
      ),
    },
  };


  return (
    <Con>
      <Title>
        <h1>{tr("User Management")}</h1>
        <p style={{ marginLeft: "auto", color: "#666" }}>
          {tr("Super Admin Tools - Manage User Accounts")}
        </p>
      </Title>
      
      {/* Business Filter Dropdown */}
      <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "1rem" }}>
        <label style={{ fontWeight: "bold", minWidth: "120px" }}>
          {tr("Filter by Business")}:
        </label>
        <div style={{ minWidth: "300px" }}>
          <Select
            placeholder={fetchBusinesses.isLoading ? tr("Loading businesses...") : tr("Select a business...")}
            value={selectedBusiness ? { 
              value: selectedBusiness, 
              label: fetchBusinesses?.data?.find((b: Business) => b.id === selectedBusiness)?.name 
                ? `${fetchBusinesses?.data?.find((b: Business) => b.id === selectedBusiness)?.name} (${fetchBusinesses?.data?.find((b: Business) => b.id === selectedBusiness)?.user_count} ${tr("users")})`
                : selectedBusiness 
            } : null}
            onChange={(option: any) => setSelectedBusiness(option?.value || null)}
            options={fetchBusinesses?.data?.map((business: Business) => ({
              value: business.id,
              label: `${business.name} (${business.user_count} ${tr("users")})`
            })) || []}
            isClearable
          />
        </div>
        {selectedBusiness && (
          <Button
            size="small"
            onClick={() => setSelectedBusiness(null)}
          >
            {tr("Clear Filter")}
          </Button>
        )}
      </div>
      
      {!selectedBusiness ? (
        <div style={{ 
          textAlign: "center", 
          padding: "2rem", 
          color: "#666",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          marginTop: "1rem"
        }}>
          <p>{tr("Please select a business to view users")}</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={fetchUsers?.data?.filter((user: User) => !user.status_inactive) || []}
        />
      )}
    </Con>
  );
}

// Update User Modal Component
function UpdateUserModal({ user, onUpdate }: { user: User; onUpdate: any }) {
  const { t } = useTranslation();
  const tBase = "views.admin.userManagement";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();

  const initialValues = {
    username: user.username,
  };

  const validationSchema = Yup.object({
    username: Yup.string()
      .required(tr("Username is required"))
      .min(3, tr("Username must be at least 3 characters"))
      .matches(/^[a-zA-Z0-9_.]+$/, tr("Username can only contain letters, numbers, underscores, and decimals")),
  });

  const handleSubmit = async (values: any, { setSubmitting, setFieldError }: any) => {
    try {
      await onUpdate.mutateAsync({
        userId: user.user_id,
        data: {
          username: values.username,
        },
      });
    } catch (error: any) {
      if (error.response?.data === "username") {
        setFieldError("username", tr("Username already exists"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContent>
      <UserInfo>
        <Avatar showLabel user_id={user.user_id}>
          {user.full_name}
        </Avatar>
        <div>
          <div><strong>{user.full_name}</strong></div>
          <div style={{ color: "#666" }}>{user.email}</div>
        </div>
      </UserInfo>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors, isSubmitting, values, handleChange, handleBlur }) => (
          <Form>
            <Input
              name="username"
              label={tr("Username")}
              value={values.username}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.username && errors.username ? String(errors.username) : undefined}
              required
            />
            
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
              >
                {isSubmitting ? tr("Updating...") : tr("Update User")}
              </Button>
              <Button
                type="button"
                onClick={() => setModal()}
                disabled={isSubmitting}
              >
                {tr("Cancel")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
}

// Change Company Modal Component
function ChangeCompanyModal({ user, onUpdate, businesses }: { user: User; onUpdate: any; businesses: Business[] }) {
  const { t } = useTranslation();
  const tBase = "views.admin.userManagement";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();

  const initialValues = {
    newCompanyId: "",
  };

  const validationSchema = Yup.object({
    newCompanyId: Yup.string()
      .required(tr("Please select a company")),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onUpdate.mutateAsync({
        userId: user.user_id,
        newCompanyId: values.newCompanyId,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContent>
      <UserInfo>
        <Avatar showLabel user_id={user.user_id}>
          {user.full_name}
        </Avatar>
        <div>
          <div><strong>{user.full_name}</strong></div>
          <div style={{ color: "#666" }}>{user.username}</div>
        </div>
      </UserInfo>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors, isSubmitting, values, handleChange, handleBlur }) => (
          <Form>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                {tr("Select New Company")}:
              </label>
              <Select
                name="newCompanyId"
                placeholder={tr("Choose a company...")}
                value={values.newCompanyId ? { 
                  value: values.newCompanyId, 
                  label: businesses.find((b: Business) => b.id === values.newCompanyId)?.name || values.newCompanyId 
                } : null}
                onChange={(option: any) => handleChange({ target: { name: "newCompanyId", value: option?.value || "" } })}
                options={businesses.map((business: Business) => ({
                  value: business.id,
                  label: `${business.name} (${business.user_count} ${tr("users")})`
                }))}
                isClearable
              />
              {touched.newCompanyId && errors.newCompanyId && (
                <div style={{ color: "red", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                  {String(errors.newCompanyId)}
                </div>
              )}
            </div>
            
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
              >
                {isSubmitting ? tr("Moving...") : tr("Move User")}
              </Button>
              <Button
                type="button"
                onClick={() => setModal()}
                disabled={isSubmitting}
              >
                {tr("Cancel")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
}

// Update Password Modal Component
function UpdatePasswordModal({ user, onUpdate }: { user: User; onUpdate: any }) {
  const { t } = useTranslation();
  const tBase = "views.admin.userManagement";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();

  const initialValues = {
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required(tr("Password is required"))
      .min(4, tr("Password must be at least 4 characters")),
    confirmPassword: Yup.string()
      .required(tr("Please confirm your password"))
      .oneOf([Yup.ref("newPassword")], tr("Passwords must match")),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onUpdate.mutateAsync({
        userId: user.user_id,
        newPassword: values.newPassword,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContent>
      <UserInfo>
        <Avatar showLabel user_id={user.user_id}>
          {user.full_name}
        </Avatar>
        <div>
          <div><strong>{user.full_name}</strong></div>
          <div style={{ color: "#666" }}>{user.username}</div>
        </div>
      </UserInfo>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors, isSubmitting, values, handleChange, handleBlur }) => (
          <Form>
            <Input
              name="newPassword"
              label={tr("New Password")}
              type="password"
              value={values.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.newPassword && errors.newPassword ? String(errors.newPassword) : undefined}
              required
            />
            
            <Input
              name="confirmPassword"
              label={tr("Confirm Password")}
              type="password"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPassword && errors.confirmPassword ? String(errors.confirmPassword) : undefined}
              required
            />
            
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
              >
                {isSubmitting ? tr("Updating...") : tr("Update Password")}
              </Button>
              <Button
                type="button"
                onClick={() => setModal()}
                disabled={isSubmitting}
              >
                {tr("Cancel")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
} 